import { cn } from "../utils/cn";
import pf from "./traPlatformForm.module.css";

/**
 * Edit Profile–style labeled field (compact 44px control).
 * @param {{
 *   label?: string | null;
 *   id: string;
 *   error?: string;
 *   hint?: string;
 *   required?: boolean;
 *   className?: string;
 *   inputClassName?: string;
 *   as?: "input" | "select" | "textarea";
 *   children?: import("react").ReactNode;
 * } & import("react").InputHTMLAttributes<HTMLInputElement>} props
 */
export function TRAPlatformFormField({
  label,
  id,
  error,
  hint,
  required,
  className,
  inputClassName,
  as = "input",
  children,
  ...rest
}) {
  const controlClass = cn(pf.input, error && pf.inputErr, inputClassName);
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;

  let control = children;
  if (children && as === "select") {
    control = (
      <select
        id={id}
        className={cn(pf.select, error && pf.inputErr, inputClassName)}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
        required={required}
        {...rest}
      >
        {children}
      </select>
    );
  } else if (!control) {
    if (as === "textarea") {
      control = (
        <textarea
          id={id}
          className={cn(pf.textarea, error && pf.inputErr, inputClassName)}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
          required={required}
          {...rest}
        />
      );
    } else if (as === "select") {
      control = (
        <select
          id={id}
          className={cn(pf.select, error && pf.inputErr, inputClassName)}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
          required={required}
          {...rest}
        />
      );
    } else {
      control = (
        <input
          id={id}
          className={controlClass}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
          required={required}
          {...rest}
        />
      );
    }
  }

  return (
    <div className={cn(pf.field, className)}>
      {label ? (
        <label className={pf.label} htmlFor={id}>
          {label}
          {required ? (
            <span aria-hidden="true" style={{ marginLeft: 2, color: "var(--tra-color-danger)" }}>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {control}
      {hint && !error ? (
        <p className={pf.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className={pf.fieldError} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TRAPlatformFormStack({ className, children, ...rest }) {
  return (
    <div className={cn(pf.stack, className)} data-tra-platform-form="compact" {...rest}>
      {children}
    </div>
  );
}

export { pf as traPlatformFormStyles };
